import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    budget: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    toast({
      title: "Mesajınız Alındı! ✉️",
      description: "En kısa sürede size dönüş yapacağız.",
    });
    
    // Redirect to thank you page
    setTimeout(() => {
      navigate('/tesekkurler?type=contact');
    }, 1500);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'E-posta',
      value: 'info@ruyan.com.tr',
      link: 'mailto:info@ruyan.com.tr',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Phone,
      title: 'Telefon',
      value: '0554 283 1771',
      link: 'tel:+905542831771',
      color: 'from-teal-500 to-green-500'
    },
    {
      icon: MapPin,
      title: 'Adres',
      value: 'Withco Çınarlı, 1572/1. Sk. No:33, 35170 Konak/İzmir',
      link: 'https://maps.google.com/?q=Withco+Çınarlı+1572/1+Sk+No:33+Konak+İzmir',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      value: 'Pzt-Cum: 09:00 - 18:00',
      link: '#',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const subjects = [
    'Mobil Uygulama Geliştirme',
    'Web Tasarım',
    'Yapay Zeka Entegrasyonu',
    'Dijital Pazarlama',
    'E-Ticaret',
    'Diğer'
  ];

  const budgetRanges = [
    '50.000 - 100.000 TL',
    '100.000 - 250.000 TL',
    '250.000 - 500.000 TL',
    '500.000 TL ve üzeri',
    'Belirtmek istemiyorum'
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <MessageCircle className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">İletişim</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Projenizi
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Konuşalım</span>
            </h1>

            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              Sınırlı sayıda projeyle çalışıyoruz. Her başvuruyu dikkatle değerlendiriyor,
              uygun gördüğümüz projelere 48 saat içinde geri dönüş yapıyoruz.
            </p>

            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6 max-w-2xl mx-auto">
              <p className="text-gray-300 text-sm leading-relaxed">
                <span className="text-cyan-400 font-semibold">Not:</span> Müşteri seçiminde titiz davranıyoruz.
                Veri odaklı büyüme stratejilerine inanıyor, orta-uzun vadeli iş birliğine açık olan
                ve kaliteli sonuç bekleyen firmalarla çalışıyoruz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.link}
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <info.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">{info.title}</h3>
                <p className="text-gray-400 text-sm">{info.value}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-slate-800/60 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-white mb-6">
                Başvuru Formu
              </h2>
              <p className="text-gray-400 mb-8">
                Projeniz hakkında detaylı bilgi verin. Uygun projeler için 48 saat içinde geri dönüş yapacağız.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                      placeholder="Ahmet Yılmaz"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                      placeholder="ahmet@sirket.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-gray-300 mb-2 block">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                      placeholder="0554 283 1771"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-gray-300 mb-2 block">Şirket</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                      placeholder="Şirket Adı"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-gray-300 mb-2 block">Konu *</Label>
                  <select
                    id="subject"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-600 text-white h-12 rounded-xl px-4 focus:border-cyan-500"
                    data-testid="contact-subject-select"
                  >
                    <option value="">Konu Seçiniz</option>
                    {subjects.map((subject, index) => (
                      <option key={index} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="budget" className="text-gray-300 mb-2 block">Bütçe Aralığı (Opsiyonel)</Label>
                  <select
                    id="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-600 text-white h-12 rounded-xl px-4 focus:border-cyan-500"
                    data-testid="contact-budget-select"
                  >
                    <option value="">Bütçe Seçiniz</option>
                    {budgetRanges.map((range, index) => (
                      <option key={index} value={range}>{range}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-300 mb-2 block">Mesajınız *</Label>
                  <Textarea
                    id="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white rounded-xl resize-none focus:border-cyan-500"
                    placeholder="Projeniz hakkında bize bilgi verin..."
                    data-testid="contact-message-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl hover:scale-105 transition-all"
                  data-testid="contact-submit-button"
                >
                  Mesaj Gönder
                  <Send className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </div>

            {/* Additional Info */}
            <div className="space-y-8">
              {/* Why Work With Us */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">Neden RUYAN?</h3>
                <div className="space-y-4 text-gray-400">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm">Veri odaklı, sonuç üreten büyüme sistemleri kuruyoruz</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm">AI, kullanıcı davranışı ve analytics'i birleştiriyoruz</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm">Sınırlı sayıda projeyle çalışıyor, her birine odaklanıyoruz</p>
                  </div>
                </div>
              </div>

              {/* Quick FAQ */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">Sıkça Sorulan Sorular</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-cyan-400 font-medium mb-1">Ne kadar sürede dönüş yapıyorsunuz?</h4>
                    <p className="text-gray-400 text-sm">Uygun gördüğümüz projelere 48 saat içinde dönüş yapıyoruz.</p>
                  </div>
                  <div>
                    <h4 className="text-cyan-400 font-medium mb-1">Her projeyi kabul ediyor musunuz?</h4>
                    <p className="text-gray-400 text-sm">Hayır. Veri odaklı büyümeye inanıyor ve uzun vadeli iş birliğine açık firmalarla çalışıyoruz.</p>
                  </div>
                  <div>
                    <h4 className="text-cyan-400 font-medium mb-1">İlk görüşme ücretli mi?</h4>
                    <p className="text-gray-400 text-sm">İlk keşif görüşmesi ücretsizdir.</p>
                  </div>
                </div>
              </div>

              {/* Privacy Note */}
              <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
                <p className="text-gray-400 text-sm">
                  <span className="text-cyan-400 font-semibold">Gizlilik:</span> Paylaştığınız tüm bilgiler
                  <a href="/sozlesmeler" className="text-cyan-400 hover:underline ml-1">KVKK</a> kapsamında
                  korunmaktadır. Bilgileriniz üçüncü şahıslarla paylaşılmaz.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
